import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/Themecontext';
import { adminAPI } from '../../services/api';
import { Mail, Send, Users, History, X, Check, AlertCircle, Loader } from 'lucide-react';

const AdminEmailDashboard = () => {
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState('compose');
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    recipient_email: '',
    recipient_name: '',
    subject: '',
    message: '',
    email_type: 'info',
  });

  // Bulk email state
  const [bulkEmails, setBulkEmails] = useState('');
  const [bulkSubject, setBulkSubject] = useState('');
  const [bulkMessage, setBulkMessage] = useState('');

  useEffect(() => {
    fetchEmailTemplates();
  }, []);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchEmailLogs();
    }
  }, [activeTab]);

  const fetchEmailTemplates = async () => {
    try {
      const data = await adminAPI.getEmailTemplates();
      setEmailTemplates(data || []);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
      setEmailTemplates([
        { id: 'info', name: 'Information Email', description: 'General information', icon: '📧' },
        { id: 'interview', name: 'Interview Invitation', description: 'Invite for interview', icon: '📅' },
        { id: 'offer', name: 'Job Offer', description: 'Extend job offer', icon: '🎉' },
        { id: 'rejection', name: 'Application Rejection', description: 'Inform about rejection', icon: '📋' },
        { id: 'custom', name: 'Custom Message', description: 'Send custom message', icon: '✉️' },
      ]);
    }
  };

  const fetchEmailLogs = async () => {
    setLoading(true);
    try {
      const data = await adminAPI.getEmailLogs({ page: 1, limit: 50 });
      setEmailLogs(data?.logs || []);
    } catch (err) {
      console.error('Failed to fetch logs:', err);
      setError('Failed to load email logs');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    setSuccess('');

    try {
      await adminAPI.sendCustomEmail(formData);
      setSuccess(`✅ Email sent successfully to ${formData.recipient_email}`);
      
      // Reset form
      setFormData({
        recipient_email: '',
        recipient_name: '',
        subject: '',
        message: '',
        email_type: 'info',
      });

      // Refresh logs if on history tab
      if (activeTab === 'history') {
        setTimeout(fetchEmailLogs, 1000);
      }
    } catch (err) {
      console.error('Email send error:', err);
      setError(err.response?.data?.error || 'Failed to send email. Please check your configuration.');
    } finally {
      setSending(false);
    }
  };

  const handleBulkSend = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    setSuccess('');

    // Parse emails
    const emails = bulkEmails
      .split('\n')
      .map(e => e.trim())
      .filter(e => e && e.includes('@'));

    if (emails.length === 0) {
      setError('Please enter at least one valid email address');
      setSending(false);
      return;
    }

    try {
      const result = await adminAPI.sendBulkEmail({
        recipient_emails: emails,
        subject: bulkSubject,
        message: bulkMessage,
      });

      setSuccess(
        `✅ Bulk email sent! Delivered: ${result.sent || 0}, Failed: ${result.failed || 0}`
      );

      // Reset form
      setBulkEmails('');
      setBulkSubject('');
      setBulkMessage('');
      
      // Refresh logs
      if (activeTab === 'history') {
        setTimeout(fetchEmailLogs, 1000);
      }
    } catch (err) {
      console.error('Bulk email error:', err);
      setError(err.response?.data?.error || 'Failed to send bulk emails');
    } finally {
      setSending(false);
    }
  };

  const getThemeClasses = () => {
    if (theme === 'light') {
      return {
        bg: 'bg-gray-50',
        card: 'bg-white',
        border: 'border-gray-200',
        text: 'text-gray-900',
        textMuted: 'text-gray-600',
        input: 'bg-white border-gray-300 text-gray-900',
        button: 'bg-gradient-to-r from-purple-600 to-pink-600',
        tabActive: 'bg-purple-600 text-white',
        tabInactive: 'text-gray-600 hover:bg-gray-100',
      };
    }
    return {
      bg: 'bg-black',
      card: 'bg-zinc-900/50 backdrop-blur-xl',
      border: 'border-white/10',
      text: 'text-white',
      textMuted: 'text-zinc-400',
      input: 'bg-black/50 border-white/10 text-white',
      button: 'bg-gradient-to-r from-purple-600 to-pink-600',
      tabActive: 'bg-gradient-to-r from-purple-600 to-pink-600 text-white',
      tabInactive: 'text-zinc-400 hover:bg-white/5',
    };
  };

  const classes = getThemeClasses();

  return (
    <div className={`${classes.bg} min-h-screen p-4 sm:p-6 md:p-10`}>
      <div className="max-w-6xl mx-auto space-y-4 md:space-y-6">
        {/* Header */}
        <div>
          <h1 className={`text-2xl sm:text-3xl font-black ${classes.text} flex items-center gap-3`}>
            <Mail className="text-purple-500" size={28} />
            Email Management
          </h1>
          <p className={`${classes.textMuted} text-sm sm:text-base mt-1`}>Send custom emails and manage communication</p>
        </div>

        {/* Notifications */}
        {success && (
          <div className="p-3 sm:p-4 bg-green-500/10 border border-green-500/50 rounded-xl text-green-500 flex items-center gap-3 text-sm sm:text-base">
            <Check size={20} className="flex-shrink-0" />
            <span className="flex-1">{success}</span>
            <button onClick={() => setSuccess('')} className="ml-auto flex-shrink-0">
              <X size={18} />
            </button>
          </div>
        )}

        {error && (
          <div className="p-3 sm:p-4 bg-red-500/10 border border-red-500/50 rounded-xl text-red-500 flex items-center gap-3 text-sm sm:text-base">
            <AlertCircle size={20} className="flex-shrink-0" />
            <span className="flex-1">{error}</span>
            <button onClick={() => setError('')} className="ml-auto flex-shrink-0">
              <X size={18} />
            </button>
          </div>
        )}

        {/* Tabs - Responsive */}
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={() => setActiveTab('compose')}
            className={`px-4 sm:px-6 py-3 rounded-xl font-bold text-xs sm:text-sm uppercase tracking-wider transition-all ${
              activeTab === 'compose' ? classes.tabActive : classes.tabInactive
            }`}
          >
            <Send size={16} className="inline mr-2" />
            Compose
          </button>
          <button
            onClick={() => setActiveTab('bulk')}
            className={`px-4 sm:px-6 py-3 rounded-xl font-bold text-xs sm:text-sm uppercase tracking-wider transition-all ${
              activeTab === 'bulk' ? classes.tabActive : classes.tabInactive
            }`}
          >
            <Users size={16} className="inline mr-2" />
            Bulk Send
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 sm:px-6 py-3 rounded-xl font-bold text-xs sm:text-sm uppercase tracking-wider transition-all ${
              activeTab === 'history' ? classes.tabActive : classes.tabInactive
            }`}
          >
            <History size={16} className="inline mr-2" />
            History
          </button>
        </div>

        {/* Compose Single Email */}
        {activeTab === 'compose' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            {/* Form */}
            <div className="lg:col-span-2">
              <div className={`${classes.card} border ${classes.border} rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-8`}>
                <h2 className={`text-lg sm:text-xl font-bold ${classes.text} mb-4 sm:mb-6`}>Send Custom Email</h2>
                
                <form onSubmit={handleSendEmail} className="space-y-4 sm:space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-xs sm:text-sm font-medium ${classes.text} mb-2`}>
                        Recipient Email *
                      </label>
                      <input
                        type="email"
                        value={formData.recipient_email}
                        onChange={(e) => setFormData({...formData, recipient_email: e.target.value})}
                        required
                        className={`w-full p-3 border ${classes.input} rounded-xl focus:outline-none focus:border-purple-500 text-sm sm:text-base`}
                        placeholder="user@example.com"
                      />
                    </div>

                    <div>
                      <label className={`block text-xs sm:text-sm font-medium ${classes.text} mb-2`}>
                        Recipient Name *
                      </label>
                      <input
                        type="text"
                        value={formData.recipient_name}
                        onChange={(e) => setFormData({...formData, recipient_name: e.target.value})}
                        required
                        className={`w-full p-3 border ${classes.input} rounded-xl focus:outline-none focus:border-purple-500 text-sm sm:text-base`}
                        placeholder="John Doe"
                      />
                    </div>
                  </div>

                  <div>
                    <label className={`block text-xs sm:text-sm font-medium ${classes.text} mb-2`}>
                      Email Template
                    </label>
                    <select
                      value={formData.email_type}
                      onChange={(e) => setFormData({...formData, email_type: e.target.value})}
                      className={`w-full p-3 border ${classes.input} rounded-xl focus:outline-none focus:border-purple-500 text-sm sm:text-base`}
                    >
                      {emailTemplates.map(template => (
                        <option key={template.id} value={template.id}>
                          {template.icon} {template.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={`block text-xs sm:text-sm font-medium ${classes.text} mb-2`}>
                      Subject *
                    </label>
                    <input
                      type="text"
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                      required
                      className={`w-full p-3 border ${classes.input} rounded-xl focus:outline-none focus:border-purple-500 text-sm sm:text-base`}
                      placeholder="Email subject line"
                    />
                  </div>

                  <div>
                    <label className={`block text-xs sm:text-sm font-medium ${classes.text} mb-2`}>
                      Message *
                    </label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      required
                      rows={6}
                      className={`w-full p-3 border ${classes.input} rounded-xl focus:outline-none focus:border-purple-500 text-sm sm:text-base`}
                      placeholder="Write your message here..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={sending}
                    className={`w-full py-3 sm:py-4 rounded-xl font-bold text-white ${classes.button} shadow-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2 text-sm sm:text-base`}
                  >
                    {sending ? (
                      <>
                        <Loader className="w-5 h-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={20} />
                        Send Email
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>

            {/* Templates Sidebar - Hidden on mobile, shown on large screens */}
            <div className="hidden lg:block">
              <div className={`${classes.card} border ${classes.border} rounded-3xl p-6`}>
                <h3 className={`text-lg font-bold ${classes.text} mb-4`}>Templates</h3>
                <div className="space-y-3">
                  {emailTemplates.map(template => (
                    <div
                      key={template.id}
                      className={`p-4 rounded-xl border ${classes.border} hover:bg-purple-500/10 transition-colors cursor-pointer`}
                      onClick={() => setFormData({...formData, email_type: template.id})}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{template.icon}</span>
                        <div>
                          <p className={`font-bold text-sm ${classes.text}`}>{template.name}</p>
                          <p className={`text-xs ${classes.textMuted}`}>{template.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Send */}
        {activeTab === 'bulk' && (
          <div className={`${classes.card} border ${classes.border} rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-8 max-w-3xl mx-auto`}>
            <h2 className={`text-lg sm:text-xl font-bold ${classes.text} mb-4 sm:mb-6`}>Send Bulk Emails</h2>
            
            <form onSubmit={handleBulkSend} className="space-y-4 sm:space-y-6">
              <div>
                <label className={`block text-xs sm:text-sm font-medium ${classes.text} mb-2`}>
                  Recipient Emails (one per line) *
                </label>
                <textarea
                  value={bulkEmails}
                  onChange={(e) => setBulkEmails(e.target.value)}
                  required
                  rows={6}
                  className={`w-full p-3 border ${classes.input} rounded-xl focus:outline-none focus:border-purple-500 font-mono text-xs sm:text-sm`}
                  placeholder={`user1@example.com\nuser2@example.com\nuser3@example.com`}
                />
                <p className={`text-xs ${classes.textMuted} mt-2`}>
                  {bulkEmails.split('\n').filter(e => e.trim() && e.includes('@')).length} valid emails
                </p>
              </div>

              <div>
                <label className={`block text-xs sm:text-sm font-medium ${classes.text} mb-2`}>
                  Subject *
                </label>
                <input
                  type="text"
                  value={bulkSubject}
                  onChange={(e) => setBulkSubject(e.target.value)}
                  required
                  className={`w-full p-3 border ${classes.input} rounded-xl focus:outline-none focus:border-purple-500 text-sm sm:text-base`}
                  placeholder="Bulk email subject"
                />
              </div>

              <div>
                <label className={`block text-xs sm:text-sm font-medium ${classes.text} mb-2`}>
                  Message *
                </label>
                <textarea
                  value={bulkMessage}
                  onChange={(e) => setBulkMessage(e.target.value)}
                  required
                  rows={6}
                  className={`w-full p-3 border ${classes.input} rounded-xl focus:outline-none focus:border-purple-500 text-sm sm:text-base`}
                  placeholder="Write your bulk message here..."
                />
              </div>

              <button
                type="submit"
                disabled={sending}
                className={`w-full py-3 sm:py-4 rounded-xl font-bold text-white ${classes.button} shadow-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2 text-sm sm:text-base`}
              >
                {sending ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Users size={20} />
                    Send to All
                  </>
                )}
              </button>
            </form>
          </div>
        )}

        {/* Email History - Responsive Table */}
        {activeTab === 'history' && (
          <div className={`${classes.card} border ${classes.border} rounded-2xl md:rounded-3xl overflow-hidden`}>
            <div className="p-4 sm:p-6 border-b border-white/10">
              <h2 className={`text-lg sm:text-xl font-bold ${classes.text}`}>Email History</h2>
            </div>
            
            {loading ? (
              <div className="p-12 text-center">
                <Loader className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto" />
                <p className={`mt-4 ${classes.textMuted}`}>Loading logs...</p>
              </div>
            ) : emailLogs.length === 0 ? (
              <div className="p-12 text-center">
                <Mail size={48} className={`${classes.textMuted} mx-auto mb-4`} />
                <p className={classes.textMuted}>No email logs yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`text-[10px] sm:text-xs font-bold uppercase ${classes.textMuted} border-b ${classes.border}`}>
                    <tr>
                      <th className="p-3 sm:p-4 text-left">Recipient</th>
                      <th className="p-3 sm:p-4 text-left hidden sm:table-cell">Subject</th>
                      <th className="p-3 sm:p-4 text-left">Type</th>
                      <th className="p-3 sm:p-4 text-left">Sent At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emailLogs.map((log, index) => (
                      <tr key={index} className={`border-b ${classes.border} hover:bg-white/5`}>
                        <td className="p-3 sm:p-4">
                          <div>
                            <p className={`font-bold text-sm ${classes.text}`}>{log.recipient_name}</p>
                            <p className={`text-xs ${classes.textMuted} truncate max-w-[150px]`}>{log.recipient_email}</p>
                          </div>
                        </td>
                        <td className="p-3 sm:p-4 hidden sm:table-cell">
                          <p className={`${classes.text} text-sm`}>{log.subject}</p>
                        </td>
                        <td className="p-3 sm:p-4">
                          <span className="px-2 sm:px-3 py-1 bg-purple-500/20 text-purple-500 rounded-full text-[10px] sm:text-xs font-bold uppercase">
                            {log.email_type || 'info'}
                          </span>
                        </td>
                        <td className="p-3 sm:p-4">
                          <p className={`text-xs ${classes.textMuted}`}>
                            {new Date(log.sent_at).toLocaleDateString()}
                          </p>
                          <p className={`text-[10px] ${classes.textMuted}`}>
                            {new Date(log.sent_at).toLocaleTimeString()}
                          </p>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEmailDashboard;